import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportDviBaseTemplate, reportDviFilterOptions } from "../report-dvi";
import { ReportDviFilterSettingsForm } from "../settings-form/ReportDviFilterSettingsForm";
import { ReportDviFilterSettings } from "../settings/ReportDviFilterSettings";
import { ReportDviTableDataRequest } from "../data-request/ReportDviTableDataRequest";
import { ReportDviJournalRequest } from "../journal/ReportDviJournalRequest";
import { ReportDviRow } from "../table-data/ReportDviRow";
import { Option } from "../../../common/types/Option";
import { MutuallyRedeemingLevel } from "../../../common/types/roots/fields";

export class ReportDviFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportDviFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'DVI') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'DVI') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        
        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        let paymentTypeOption = options.paymentTypes.find(it => it.value === settings.paymentType)
        if (!paymentTypeOption) {
            throw new Error('error_not_found_payment_option')
        }
        paymentTypeOption = { ...paymentTypeOption }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        // 2. Формирование
        const form: ReportDviFilterSettingsForm = {
            // Base:
            key: 'DVI',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            periodFrom: new Date(settings.period.from),
            periodTo: new Date(settings.period.to),
            measureUnit: measureUnitOption,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            budgetTypes: budgetTypeOptions,
            region: regionOption,
            paymentType: paymentTypeOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'DVI') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.periodFrom || !form.periodTo) {
            throw new Error('error_input_period')
        }
        
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.mutuallyRedeemingLevels == null) {
            throw new Error('error_input_mutually_redeeming_levels')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')

        const settings: ReportDviFilterSettings = {
            // Base:
            reportName: 'DVI',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            period: { from: strPeriodFrom, to: strPeriodTo },
            budgetTypes: form.budgetTypes.map(it => it.value),
            region: form.region.value,
            paymentType: form.paymentType.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            incomes: cloneDeep(form.incomes),
            expenses: cloneDeep(form.expenses)
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportDviFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()
        const firstJan = moment().startOf('year').toDate()

        const baseFS = reportDviBaseTemplate.filterSettings
        if (baseFS.reportName !== 'DVI') {
            throw Error('error_invalid_report_key')
        }

        const emptyForm: ReportDviFilterSettingsForm = {
            key: 'DVI',
            // Base fields
            reportName: options.reportNames.find(it => it.value === baseFS.reportName)!,
            reportType: options.reportTypes.find(it => it.value === baseFS.reportType)!,
            classificationType: options.classificationTypes.find(it => it.value === baseFS.classificationType)!,
            dataSource: options.dataSources.find(it => it.value === baseFS.dataSource)!,
            periodicity: options.periodicities.find(it => it.value === baseFS.periodicity)!,
            // Custom fields            
            periodFrom: firstJan,
            periodTo: yesterdayDate,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            mutuallyRedeemingLevels: [],
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            budgetTypes: [],
            region: null,
            paymentType: options.paymentTypes.find(it => it.value === 'OBLIGATIONS')!,
            roundUp: false,
            roundUpTo: null,
            incomes: cloneDeep(options.incomes),
            expenses: cloneDeep(options.expenses),
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'DVI') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.periodFrom || !form.periodTo) {
            throw new Error('error_input_period')
        }

        if (form.periodFrom > form.periodTo) {
            throw new Error('error_period_range_invalid')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_input_budget_types')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.mutuallyRedeemingLevels.length == 0) {
            throw new Error('error_input_mutually_redeeming_levels')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')

        const classficationType = form.classificationType.value
        if (classficationType === 'MIXED') {
            throw new Error('error_unsupported_class_type')
        }

        let activeIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        let activeExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        let classificators: string[] = []
        if (classficationType === 'INCOMES') {
            activeExpenses = []
            classificators = activeIncomes.map(it => it.key)
        } else if (classficationType === 'EXPENSES') {
            activeIncomes = []
            classificators = activeExpenses.map(it => it.key)
        }

        const params: ReportDviTableDataRequest = {
            bdate: strPeriodFrom,
            edate: strPeriodTo,
            budget_types: form.budgetTypes.map(it => it.value),
            region: form.region.value,
            classification_type: classficationType,
            payment_type: form.paymentType.value,
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up: roundUpTo,
            incomes: activeIncomes,
            expenses: activeExpenses
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/dvi',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData: any[] = await response.json()

        const tableData: ReportDviRow[] = responseTableData.map((it: any, i: number) => {
            const row: ReportDviRow = {
                index: i,
                gr: it.gr ?? null,
                pgr: it.pgr ?? null,
                abp: it.abp ?? null,
                prg: it.prg ?? null,
                ppr: it.ppr ?? null,
                spf: it.spf ?? null,

                kat: it.kat ?? null,
                cls: it.cls ?? null,
                pcl: it.pcl ?? null,

                name: it.name ?? null,
                plan: it.plan,
                month1: it['1'],
                month2: it['2'],
                month3: it['3'],
                month4: it['4'],
                month5: it['5'],
                month6: it['6'],
                month7: it['7'],
                month8: it['8'],
                month9: it['9'],
                month10: it['10'],
                month11: it['11'],
                month12: it['12'],
                correctedPlan: it.corrected_plan
            }
            return row
        })

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportDviJournalRequest = {
            reportName: "DVI", // Приходится задавать для поддержки generics
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            bdate: strPeriodFrom,
            edate: strPeriodTo,
            classification_type: classficationType,
            classificators_order: classificators,
            payment_type: form.paymentType.value,
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up: roundUpTo,
            table_data: responseTableData
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
